import {Box, Button, debounce, SxProps, Theme, Typography} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {CreditCardOutlined} from '@mui/icons-material';
import {GridSortModel} from '@mui/x-data-grid';
import {FC, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {tagsApi} from 'src/api/tags';
import {TagOwnerType, TagsFilterValue} from 'src/types/tags';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {RfidTableForDedicatedPage} from 'src/components/common/RfidTableForDedicatedPage';
import {TagStatsStrip} from 'src/components/rfid/TagStatsStrip';
import {TagFlag} from 'src/components/rfid/TagStatsStrip/TagStatsStrip.types';
import {RfidHolderStrip} from 'src/components/rfid/RfidHolderStrip';
import {HolderStatus} from 'src/components/rfid/RfidHolderStrip/RfidHolderStrip.types';
import {CreateRfidFormFromDedicatedPage} from 'src/components/forms/CreateRfidFormFromDedicatedPage';
import {RequestRfidFormFromDedicatedPage} from 'src/components/forms/RequestRfidFormFromDedicatedPage';
import {useListQueryState} from 'src/hooks/useListQueryState';

/**
 * The block and the list are two cards on one page. The top margin is the app bar's: the table used
 * to be first and carried its own, and now that the block is above it the clearance lives here.
 */
const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

const DEFAULT_PAGE_SIZE = 100;

/**
 * Everything this screen can be looking at, and the only copy of it: each field lives in the query
 * string, so opening a card and coming back restores the view instead of resetting it.
 *
 * `blocked` is three-valued - unset, switched off, working - so it travels as the string it is
 * written as rather than as a boolean, whose absence and whose `false` would be the same URL.
 * `master` has only two, but shares the shape so that both read the same way below.
 */
const QUERY_DEFAULTS = {
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    keyword: '',
    sortField: '',
    sortAsc: false,
    blocked: '',
    master: '',
    ownerType: '',
};

export const Rfid: FC = () => {
    const intl = useIntl();
    const persona = usePersona();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const isSuperAdmin = persona.isPlatformAdmin;
    // The platform administrator reads this list as a business does - it is one business's tags -
    // so they take the same branch rather than a thinner one of their own.
    const isBusinessUser = persona.isBackOffice;
    const isCompanyUser = persona.isCompany;
    const isEmployeeUser = persona.isEmployee;
    // Drivers and employees get the holder's page: their own cards and what they can do about one.
    // The account type decides the page's shape; what the buttons on it may do is still permissions.
    const holderView = persona.isDriver || persona.isEmployee;
    // By permission, not by persona. Hiding the add button from employees alone left it on screen
    // for a driver, who has no rfidTags.create and gets a refusal the moment they use it.
    const canCreateTag = useCan('rfidTags.create');
    const companyId = authInfo?.company?.id || authInfo?.employee?.company?.id;
    const employeeId = authInfo?.employee?.id;



    const [showAddRfidModal, setShowAddRfidModal] = useState<boolean>(false);
    const [showRequestRfidModal, setShowRequestRfidModal] = useState<boolean>(false);
    const [query, setQuery] = useListQueryState(QUERY_DEFAULTS);
    const keyword = query.keyword;

    // Derived from the address bar rather than kept beside it. The strip's tiles and the toolbar's
    // dropdowns both write here, and the three fields are read back by name.
    const filterValue: TagsFilterValue = useMemo(() => ({
        blocked: query.blocked === '' ? undefined : query.blocked === 'true',
        master: query.master === 'true' ? true : undefined,
        ownerType: (query.ownerType || undefined) as TagOwnerType | undefined,
    }), [query.blocked, query.master, query.ownerType]);

    const {data} = tagsApi.useGetTagsQuery({
        params: {
            page: query.page,
            size: query.size,
        },
        data: {
            businessId: businessId,
            sortAsc: query.sortAsc,
            sortField: query.sortField === 'idTag' ? 'ID_TAG' : 'CREATED_AT',
            keyword,
            blocked: filterValue.blocked,
            ownerType: filterValue.ownerType,
            master: filterValue.master,
            companyId: companyId ? companyId : undefined,
            employeeId: isEmployeeUser ? employeeId : undefined,
        },
    });

    // The cells' own filters are left out: the server ignores them anyway, and keeping them out of
    // the key means clicking a cell does not refetch numbers that cannot have changed.
    const {data: tagStats} = tagsApi.useGetTagStatsQuery({
        businessId,
        keyword,
        companyId: companyId ? companyId : undefined,
        employeeId: isEmployeeUser ? employeeId : undefined,
    });

    // Owner and flag are alternatives, not filters to combine: each selection clears the other, and
    // clicking the active one clears it.
    const handleSelectOwnerType = (type: TagOwnerType) => {
        // The value passed is the whole filter, so what it leaves out is cleared.
        handleFilter({
            ownerType: filterValue.ownerType === type ? undefined : type,
        });
    };

    const handleSelectFlag = (flag: TagFlag) => {
        const active = flag === 'blocked' ? filterValue.blocked : filterValue.master;
        handleFilter({
            blocked: !active && flag === 'blocked' ? true : undefined,
            master: !active && flag === 'master' ? true : undefined,
        });
    };

    // Working or switched off: one axis, so picking one replaces the other and picking it again clears it.
    const handleSelectStatus = (status: HolderStatus) => {
        const blocked = status === 'deactivated';
        handleFilter({blocked: filterValue.blocked === blocked ? undefined : blocked});
    };

    const handleSearch = (value: string) => setQuery({...query, page: 0, keyword: value});

    /**
     * Back to the first page: a filter that narrows the list from four pages to one leaves you on
     * page three of a one-page result, which reads as an empty list rather than as a narrower one.
     *
     * A flag that is not in the value given is cleared, which is what replacing the whole filter
     * object used to do - the toolbar sends owner and blocked together and has no master of its own.
     */
    const handleFilter = (value: TagsFilterValue) => setQuery({
        ...query,
        page: 0,
        blocked: value.blocked === undefined ? '' : String(value.blocked),
        master: value.master ? 'true' : '',
        ownerType: value.ownerType ?? '',
    });

    const handleShowAddRfidModal = (isActive: boolean) => () => {
        setShowAddRfidModal(isActive);
    };

    const handleShowRequestRfidModal = (isActive: boolean) => () => {
        setShowRequestRfidModal(isActive);
    };

    const handleChangePage = (page: number) => setQuery({...query, page});

    const handleChangeRowsNumber = (size: number) => {
        debounce(() => setQuery({...query, page: 0, size: size || DEFAULT_PAGE_SIZE}), 1_000)();
    };

    const handleSort = (value: GridSortModel) => setQuery({
        ...query,
        sortField: value[0]?.field ?? '',
        sortAsc: value[0]?.sort === 'asc',
    });

    const requestRfidButton = isCompanyUser ? (
        <Button
            type="button"
            color="secondary"
            variant="outlined"
            onClick={handleShowRequestRfidModal(true)}
            sx={{
                height: '36px',
                borderRadius: '8px',
                textTransform: 'none',
                whiteSpace: 'nowrap',
            }}
        >
            <Typography variant="button">
                <FormattedMessage id="requestRfidButtonLabel"/>
            </Typography>
        </Button>
    ) : undefined;

    return (
        <Box sx={pageSx}>
            {/* Counts by owner and flag answer a back office's questions; a holder with a card or two
                has none of them. They get the same strip with their own tiles: how many cards work,
                how many are switched off, and the two things they can do about a card. */}
            {holderView && (
                <RfidHolderStrip
                    stats={tagStats}
                    businessId={businessId}
                    companyId={companyId ? companyId : undefined}
                    employeeId={isEmployeeUser ? employeeId : undefined}
                    activeStatus={
                        filterValue.blocked === true ? 'deactivated' : filterValue.blocked === false ? 'active' : undefined
                    }
                    onSelectStatus={handleSelectStatus}
                />
            )}
            {!holderView && (
                <TagStatsStrip
                    data={tagStats}
                    activeOwnerType={filterValue.ownerType}
                    onSelectOwnerType={handleSelectOwnerType}
                    activeFlag={
                        filterValue.blocked ? 'blocked' : filterValue.master ? 'master' : undefined
                    }
                    onSelectFlag={handleSelectFlag}
                />
            )}
            {data && (
                <RfidTableForDedicatedPage
                    data={data}
                    handleSort={handleSort}
                    handleChangePage={handleChangePage}
                    handleChangeRowsNumber={handleChangeRowsNumber}
                    handleSearch={handleSearch}
                    handleFilter={handleFilter}
                    isSuperAdmin={isSuperAdmin}
                    isBusinessUser={isBusinessUser}
                    isCompanyUser={isCompanyUser}
                    isEmployeeUser={isEmployeeUser}
                    holderView={holderView}
                    filters={filterValue}
                    searchValue={keyword}
                    title={intl.formatMessage({id: 'rfidPageHeader'})}
                    buttonText="addRfidButtonLabel"
                    onAddClick={canCreateTag ? handleShowAddRfidModal(true) : undefined}
                    onlyTitle={!canCreateTag}
                    icon={<CreditCardOutlined/>}
                    extraHeaderAction={requestRfidButton}
                />
            )}

            <AMPModal
                handleClose={handleShowAddRfidModal(false)}
                open={showAddRfidModal}
                title={intl.formatMessage({id: 'addRfid'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <CreateRfidFormFromDedicatedPage handleClose={handleShowAddRfidModal(false)}/>
            </AMPModal>

            <AMPModal
                handleClose={handleShowRequestRfidModal(false)}
                open={showRequestRfidModal}
                title={intl.formatMessage({id: 'requestRfidButtonLabel'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <RequestRfidFormFromDedicatedPage handleClose={handleShowRequestRfidModal(false)}/>
            </AMPModal>
        </Box>
    );
};
